import { Row, Space } from 'antd';
import { useFormik } from 'formik';
import { EditOutlined } from '@ant-design/icons';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import CustomerSelect from 'components/new-ui/composed/selects/CustomerSelect';
import IncotermSelect from 'components/new-ui/composed/selects/IncotermSelect';
import { JobResponse } from 'store/jobs/job';
import { validationSchema } from './validationSchema';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import Notification from 'components/ui/base/notification/Notification';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useJobs from 'hooks/useJobs';

type Props = {
    open: boolean;
    job: JobResponse;
    onClose: () => void;
    onSuccess?: () => void;
};

const EditJobModal = ({ open, job, onClose, onSuccess }: Props) => {
    const { editJob } = useJobs();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const formik = useFormik({
        initialValues: {
            customerId: job?.customer?.id ?? job?.customerId ?? '',
            jobReferenceId: job?.referenceId ?? '',
            incotermCode: job?.incotermCode ?? '',
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                showGlobalOverlay({ type: 'LoadingOverlay' });

                await editJob(job.id, {
                    incotermCode: values.incotermCode,
                    customerId: values.customerId,
                });

                Notification({
                    type: 'success',
                    messageTitle: 'Update Complete',
                    description: 'Successfully updated job details.',
                });

                onClose();
            } catch (err) {
                const errorMessage = getErrorMessage(err) || DEFAULT_INTERNAL_ERROR_MESSAGE;
                return Notification({
                    type: 'error',
                    messageTitle: 'Update Failed',
                    description: errorMessage,
                });
            } finally {
                hideGlobalOverlay();
            }
        },
    });

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    const handleSubmit = async () => {
        await formik.submitForm();
        onSuccess?.();
    };

    return (
        <ConfirmationModal
            title="Edit Job Details"
            confirmButtonText="Save Changes"
            icon={<EditOutlined />}
            open={open}
            onClose={handleClose}
            onCancel={handleClose}
            onConfirm={handleSubmit}
            confirmationButtonLoading={formik.isSubmitting}
            content={
                <Space size={12} direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                    <Row justify="space-between">
                        <Space size={8}>
                            <LabelRequiredSymbol />
                            <label>Job ID :</label>
                        </Space>
                        <FormInput
                            inputStyles={{ width: 375 }}
                            fieldProps={formik.getFieldProps('jobReferenceId')}
                            disabled
                        />
                    </Row>

                    <Row justify="space-between">
                        <Space size={8}>
                            <LabelRequiredSymbol />
                            <label htmlFor="customerId">Customer :</label>
                        </Space>
                        <div style={{ width: 375 }}>
                            <CustomerSelect
                                customerId={formik.values.customerId}
                                fieldMeta={formik.getFieldMeta('customerId')}
                                fieldHelper={formik.getFieldHelpers('customerId')}
                                onChange={(val) => formik.setFieldValue('customerId', val)}
                                onClear={() => formik.setFieldValue('customerId', undefined)}
                            />
                        </div>
                    </Row>

                    <Row justify="space-between">
                        <Space size={8}>
                            <LabelRequiredSymbol />
                            <label>Delivery Terms :</label>
                        </Space>
                        <div style={{ width: 375 }}>
                            <IncotermSelect
                                fieldMeta={formik.getFieldMeta('incotermCode')}
                                fieldHelper={formik.getFieldHelpers('incotermCode')}
                                fieldProps={formik.getFieldProps('incotermCode')}
                            />
                        </div>
                    </Row>
                </Space>
            }
        />
    );
};

export default EditJobModal;
